import {
  PROTOCOL_VERSION,
  REPLACED_CLOSE_CODE,
  ServerMessage,
  parseMessage,
  type ClientMessage,
} from '@viewer/protocol';
import type { DeviceIdentity } from '../tauri/commands';

export type SignalingStatus = 'connecting' | 'online' | 'offline';

export interface IdentityStorage {
  load(): Promise<DeviceIdentity | null>;
  save(identity: DeviceIdentity): Promise<void>;
}

export interface SignalingClientOptions {
  url: string;
  deviceName: string;
  canHost: boolean;
  identity: IdentityStorage;
  /** Token de login do colaborador, lido a cada `hello` (muda ao entrar/sair da conta). */
  userToken: () => string | null;
  onStatus(status: SignalingStatus): void;
  onMessage(message: ServerMessage): void;
  onDeviceId(deviceId: string): void;
  log?: (msg: string, ...rest: unknown[]) => void;
}

const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

/**
 * Conexão WebSocket com o servidor de signaling.
 *
 * Cuida de registrar o dispositivo na primeira vez, autenticar com `hello`
 * e reconectar com espera progressiva quando a conexão cai.
 */
export class SignalingClient {
  private socket: WebSocket | null = null;
  private reconnectDelay = RECONNECT_MIN_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private awaitingRegistration = false;
  private readonly log: (msg: string, ...rest: unknown[]) => void;

  constructor(private readonly options: SignalingClientOptions) {
    this.log = options.log ?? (() => {});
  }

  connect() {
    this.closed = false;
    this.open();
  }

  close() {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
  }

  /** Derruba e reabre a conexão para refazer o `hello` (ex.: depois de entrar ou sair da conta). */
  reconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.reconnectDelay = RECONNECT_MIN_MS;
    this.closed = false;
    this.open();
  }

  get isOnline() {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  send(message: ClientMessage) {
    if (!this.isOnline) {
      this.log('signaling: tentativa de envio sem conexão', message.type);
      return false;
    }
    this.socket!.send(JSON.stringify(message));
    return true;
  }

  private open() {
    this.options.onStatus('connecting');
    const socket = new WebSocket(this.options.url);
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectDelay = RECONNECT_MIN_MS;
      void this.authenticate();
    };

    socket.onmessage = (event) => {
      const parsed = parseMessage(ServerMessage, event.data as string);
      if (!parsed.ok) {
        this.log('signaling: mensagem inválida do servidor', parsed.error);
        return;
      }
      this.handle(parsed.value);
    };

    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.options.onStatus('offline');
      this.log('signaling: conexão fechada', event.code, event.reason);
      // Outra aba com a mesma identidade assumiu; reconectar derrubaria ela, e as duas ficariam se alternando.
      if (event.code === REPLACED_CLOSE_CODE) return;
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose é chamado logo em seguida; o log fica lá.
    };
  }

  private async authenticate() {
    const identity = await this.options.identity.load();
    if (identity) {
      this.hello(identity);
    } else {
      this.register();
    }
  }

  private register() {
    this.awaitingRegistration = true;
    this.send({ type: 'register', protocolVersion: PROTOCOL_VERSION, name: this.options.deviceName });
  }

  private hello(identity: DeviceIdentity) {
    const userToken = this.options.userToken();
    this.send({
      type: 'hello',
      protocolVersion: PROTOCOL_VERSION,
      deviceId: identity.deviceId,
      deviceSecret: identity.deviceSecret,
      name: this.options.deviceName,
      canHost: this.options.canHost,
      ...(userToken ? { userToken } : {}),
    });
  }

  private handle(message: ServerMessage) {
    switch (message.type) {
      case 'registered': {
        this.awaitingRegistration = false;
        const identity = { deviceId: message.deviceId, deviceSecret: message.deviceSecret };
        void this.options.identity.save(identity).catch((err) => this.log('falha ao salvar identidade', err));
        this.hello(identity);
        return;
      }
      case 'welcome':
        this.options.onDeviceId(message.deviceId);
        this.options.onStatus('online');
        break; // o controlador também quer o usuário e a empresa

      case 'error':
        if (message.code === 'unauthorized' && message.ref === 'hello' && !this.awaitingRegistration) {
          // A identidade guardada não é mais reconhecida (ex.: banco recriado). Registra de novo.
          this.log('signaling: identidade rejeitada, registrando novamente');
          this.register();
          return;
        }
        break;
    }
    this.options.onMessage(message);
  }

  private scheduleReconnect() {
    if (this.closed) return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS);
    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }
}
